// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import {
  assignOpenItem,
  listProjectOpenItems,
  markProjectDone,
  nudgeOpenItem,
} from "./openItemsApi";

import { type GetProjectReviewsResponse } from "../../services/askPunttProjectFeed.types";
import { useProjectReviews } from "../../utils/queries/askPunttProjects";
import { HISTORY_POLL_MS } from "../transport/mapServerMessages";

export function projectOpenItemsKey(projectId: string) {
  return ["ask-puntt-project-open-items", projectId] as const;
}

export type ProjectThreadMessageFingerprint = {
  _id: string;
  status?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
};

export function projectThreadMessagesVersion(
  messages: readonly ProjectThreadMessageFingerprint[],
): string {
  if (messages.length === 0) return "0";
  return messages
    .map(
      (message) =>
        `${message._id}:${message.status ?? ""}:${message.updatedAt ?? message.createdAt ?? ""}`,
    )
    .join("|");
}

export type UseProjectOpenItemsOptions = {
  /** When set, invalidates the open-items query whenever this version changes. */
  threadMessagesVersion?: string;
  /** Poll on HISTORY_POLL_MS while the page is visible (default true). */
  pollWhileVisible?: boolean;
};

function projectHasInFlightReview(
  reviews: GetProjectReviewsResponse | undefined,
): boolean {
  for (const item of reviews?.reviews ?? []) {
    if (item.status?.trim().toLowerCase() !== "in_review") continue;
    if ((item.aiCommentCount ?? 0) > 0) continue;
    return true;
  }
  return false;
}

function useRefetchOpenItemsWhenReviewSettles(projectId: string) {
  const queryClient = useQueryClient();
  const [pollReviews, setPollReviews] = useState(false);
  const reviewsQuery = useProjectReviews(projectId, {
    refetchInterval: pollReviews ? 5_000 : false,
  });
  const wasInFlightRef = useRef(false);
  useEffect(() => {
    const inFlight = projectHasInFlightReview(reviewsQuery.data);
    setPollReviews(inFlight);
    if (wasInFlightRef.current && !inFlight) {
      void queryClient.invalidateQueries({
        queryKey: projectOpenItemsKey(projectId),
      });
    }
    wasInFlightRef.current = inFlight;
  }, [projectId, queryClient, reviewsQuery.data]);
}

function useInvalidateOpenItemsWhenThreadChanges(
  projectId: string,
  threadMessagesVersion: string | undefined,
) {
  const queryClient = useQueryClient();
  const previousVersionRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (threadMessagesVersion == null) return;
    if (previousVersionRef.current === undefined) {
      previousVersionRef.current = threadMessagesVersion;
      return;
    }
    if (previousVersionRef.current === threadMessagesVersion) return;
    previousVersionRef.current = threadMessagesVersion;
    void queryClient.invalidateQueries({
      queryKey: projectOpenItemsKey(projectId),
    });
  }, [projectId, queryClient, threadMessagesVersion]);
}

export function useProjectOpenItems(
  projectId: string,
  options?: UseProjectOpenItemsOptions,
) {
  const queryClient = useQueryClient();
  const pollWhileVisible = options?.pollWhileVisible !== false;
  useRefetchOpenItemsWhenReviewSettles(projectId);
  useInvalidateOpenItemsWhenThreadChanges(
    projectId,
    options?.threadMessagesVersion,
  );
  const query = useQuery({
    queryKey: projectOpenItemsKey(projectId),
    queryFn: () => listProjectOpenItems(projectId),
    enabled: projectId.length > 0,
    refetchInterval: pollWhileVisible ? HISTORY_POLL_MS : false,
    refetchIntervalInBackground: false,
  });
  const assign = useMutation({
    mutationFn: (input: { itemId: string; ownerUserId: string }) =>
      assignOpenItem(projectId, input.itemId, input.ownerUserId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectOpenItemsKey(projectId),
      });
    },
  });
  const nudge = useMutation({
    mutationFn: (itemId: string) => nudgeOpenItem(projectId, itemId),
  });
  const mark = useMutation({
    mutationFn: (override: boolean) => markProjectDone(projectId, override),
    onSuccess: (result) => {
      if (!result.done) return;
      void queryClient.invalidateQueries({
        queryKey: projectOpenItemsKey(projectId),
      });
    },
  });
  return { query, mark, assign, nudge };
}

export function formatOpenItemAge(openedAt: string | Date, now: Date): string {
  const opened = openedAt instanceof Date ? openedAt : new Date(openedAt);
  const ms = Math.max(0, now.getTime() - opened.getTime());
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
